import {
	ArrowDownTrayIcon,
	ArrowPathIcon,
	CheckCircleIcon,
	ExclamationTriangleIcon,
	XCircleIcon,
} from "@heroicons/react/20/solid";
import Modal from "@/common/Modal";
import Tooltip from "@/common/tooltip/Tooltip";
import { toTitleCase } from "@/common/validation/Validators";
import { format } from "date-fns";
import { useMemo } from "react";
import { useEffect } from "react";

export function IntegrationImportSchemes({ functionality = {}, importSchemes, importingSchemes, getSchemes }) {
	const Icon = importingSchemes ? ArrowPathIcon : ArrowDownTrayIcon;

	useEffect(() => {
		if (!functionality?.polling) getSchemes();
	}, []);

	if (!functionality?.import?.schemes) return null;

	return (
		<button
			className="btn btn-default mr-3"
			disabled={importingSchemes}
			onClick={() => {
				if (functionality?.polling) getSchemes();
				else importSchemes();
			}}
		>
			<Icon className={`align-middle mr-2 h-8 w-8${importingSchemes ? " animate-spin" : ""}`} />
			Import{importingSchemes ? "ing" : null} Schemes
		</button>
	);
}

export function IntegrationOnGoingPolls({
	functionality,
	getOngoingPolls,
	onGoingPolls,
	pollInteraction,
	closeModal,
	modalStatus,
	currency,
	companyPayPeriods,
}) {
	useEffect(() => {
		if (functionality?.polling) {
			getOngoingPolls();
		}
	}, [functionality?.polling]);

	if (!onGoingPolls?.length || !functionality?.polling) return null;
	return (
		<>
			<PollInteractionModal
				currency={currency}
				closeModal={closeModal}
				modalStatus={modalStatus}
				pollInteraction={pollInteraction}
			/>
			<div className="grid grid-cols-5 gap-2 p-4 form-section">
				<h4 className="col-span-6">
					On-Going Polls
					<span className="help-block">
						These are the ongoing polls for the current integration. Polling is required due to the third
						party software taking longer than 1 minute to process the request.
					</span>
				</h4>
				<div className="grid grid-cols-subgrid col-span-6 px-4 pt-4 pb-2 bg-white rounded-t-xl">
					<div className="col-span-2">Action</div>
					<div className="col-span-4">Status</div>
				</div>
				{onGoingPolls.map((poll) => (
					<IntegrationPollRow
						companyPayPeriods={companyPayPeriods}
						poll={poll}
						pollInteraction={pollInteraction}
					/>
				))}
			</div>
		</>
	);
}

function IntegrationPollRow({
	pollInteraction,
	poll: { id, status, action, nextPollAt, expiresAt, completedAt, payPeriodId },
	companyPayPeriods,
}) {
	const StatusIcon = {
		"pending": ArrowPathIcon,
		"success": CheckCircleIcon,
		"response": CheckCircleIcon,
		"error": ExclamationTriangleIcon,
		"timed out": XCircleIcon,
	}[status];

	const dateToShow = (() => {
		if (expiresAt)
			return (
				<Tooltip content={"This poll entry will be deleted after this date"}>
					Expires at: {format(new Date(expiresAt), "do MMM, yyyy - HH:mm")}
				</Tooltip>
			);
		if (completedAt)
			return (
				<Tooltip content={"This poll entry was completed at this time"}>
					Completed at: {format(new Date(completedAt), "do MMM, yyyy - HH:mm")}
				</Tooltip>
			);
		if (nextPollAt) {
			return `Next poll at: ${format(new Date(nextPollAt), "do MMM, yyyy - HH:mm")}`;
		}
	})();

	const payPeriodName = companyPayPeriods?.find(({ id }) => id === payPeriodId)?.name;

	return (
		<div
			className="grid grid-cols-subgrid col-span-6 py-2 px-4 bg-white"
			key={id}
		>
			<div className="col-span-2">
				{payPeriodName ? `${payPeriodName}: ` : ""}
				{action}
			</div>
			<div>
				<StatusIcon className="h-6 w-6 mr-2 align-middle" />
				{toTitleCase(status)}
			</div>
			<div className="col-span-2">{dateToShow}</div>
			<div className="flex justify-end pr-4">
				<PollRowInteraction
					status={status}
					action={action}
					pollInteraction={() => pollInteraction(id, action, status)}
				/>
			</div>
		</div>
	);
}

function PollRowInteraction({ status, action, pollInteraction }) {
	switch (status) {
		case "pending":
			return (
				<button
					className="btn btn-xs btn-default"
					onClick={pollInteraction}
				>
					Check Status
				</button>
			);
		case "error":
			return (
				<button
					className="btn btn-xs btn-danger"
					onClick={pollInteraction}
				>
					View Errors
				</button>
			);
		case "response":
			return (
				<button
					className="btn btn-xs btn-success"
					onClick={pollInteraction}
				>
					{action.split(" ")[0]}
				</button>
			);
		default:
			return null;
	}
}

function PollInteractionModal({ currency, modalStatus, closeModal, pollInteraction }) {
	if (!modalStatus) return null;

	return (
		<Modal
			title={modalStatus.action}
			close={closeModal}
			width="60%"
			status={modalStatus}
		>
			<div className="p-4 px-8">
				<PayPeriods payPeriods={modalStatus.payPeriods} />
				<SchemeAndGroupsRows
					currency={currency}
					schemes={modalStatus.schemes}
					groups={modalStatus.groups}
				/>
				<ErrorRows
					errors={modalStatus.errors}
					warnings={modalStatus.warnings}
				/>
				<button
					className="btn btn-success pull-right mb-4"
					onClick={() => pollInteraction(modalStatus.id, modalStatus.action, modalStatus.status)}
				>
					{modalStatus.action.split(" ")[0]}
				</button>
			</div>
		</Modal>
	);
}

const schemeTypeOptions = [
	{ value: "banded", label: "Banded" },
	{ value: "unbanded", label: "Unbanded" },
	{ value: "type1", label: "Type 1" },
	{ value: "type2", label: "Type 2" },
	{ value: "type3", label: "Type 3" },
];

function PayPeriods({ payPeriods }) {
	if (!payPeriods?.length) return null;
	return (
		<div className="grid grid-cols-subgrid col-span-6 gap-2 p-4 form-section">
			<h4 className="col-span-6">Pay Periods</h4>
			<div className="grid grid-cols-subgrid col-span-6 px-4 pt-4 pb-2 bg-white rounded-t-xl">
				<div className="col-span-2">Pay Schedule</div>
				<div className="col-span-2">Start Date</div>
				<div className="col-span-2">End Date</div>
			</div>
			{payPeriods.map(({ paySchedule, periodStart, periodEnd }) => (
				<div className="grid grid-cols-subgrid col-span-6 px-4 pt-4 pb-2 bg-white">
					<div className="col-span-2">{paySchedule}</div>
					<div className="col-span-2">{periodStart && format(new Date(periodStart), "do MMM yyyy")}</div>
					<div className="col-span-2">{periodEnd && format(new Date(periodEnd), "do MMM yyyy")}</div>
				</div>
			))}
		</div>
	);
}

function SchemeAndGroupsRows({ currency, schemes, groups }) {
	const percentageOptions = useMemo(() => {
		return [
			{ label: "%", value: true },
			{ label: currency, value: false },
		];
	}, [currency]);

	if (!schemes?.length && !groups?.length) return null;

	return (
		<>
			{schemes?.length ? (
				<div className="grid grid-cols-subgrid col-span-6 gap-2 p-4 form-section">
					<h4 className="col-span-6">Schemes</h4>
					<div className="grid grid-cols-subgrid col-span-6 px-4 pt-4 pb-2 bg-white rounded-t-xl">
						<div className="col-span-2">Name</div>
						<div className="col-span-2">Scheme Type</div>
						<div className="col-span-2">Period</div>
					</div>
					{schemes.map(({ name, schemeType, startDate, endDate }) => (
						<div className="grid grid-cols-subgrid col-span-6 px-4 pt-4 pb-2 bg-white">
							<div className="col-span-2">{name}</div>
							<div className="col-span-2">
								{schemeTypeOptions?.find(({ value }) => value === schemeType)?.label}
							</div>
							<div className="col-span-2">
								{startDate && format(new Date(startDate), "do MMM yyyy")} -{" "}
								{endDate && endDate != "null" ? format(new Date(endDate), "do MMM yyyy") : "Now"}
							</div>
						</div>
					))}
				</div>
			) : null}
			{groups?.length ? (
				<div className="grid grid-cols-subgrid col-span-6 gap-2 p-4 form-section">
					<h4 className="col-span-6">Groups</h4>
					<div className="grid grid-cols-subgrid col-span-6 px-4 pt-4 pb-2 bg-white rounded-t-xl">
						<div className="col-span-2">Name</div>
						<div className="col-span-1">Employee</div>
						<div className="col-span-1">Employer</div>
						<div className="col-span-2">Period</div>
					</div>
					{groups.map(
						({
							name,
							startDate,
							endDate,
							employeeContribution,
							employeePercentage,
							employerContribution,
							employerPercentage,
						}) => (
							<div className="grid grid-cols-subgrid col-span-6 px-4 pt-4 pb-2 bg-white">
								<div className="col-span-2">{name}</div>
								<div className="col-span-1">
									{employeePercentage
										? `${employeeContribution}${percentageOptions?.find((p) => p.value)?.label}`
										: `${percentageOptions?.find((p) => !p.value)?.label}${employeeContribution}`}
								</div>
								<div className="col-span-1">
									{employerPercentage
										? `${employerContribution}${percentageOptions?.find((p) => p.value)?.label}`
										: `${percentageOptions?.find((p) => !p.value)?.label}${employerContribution}`}
								</div>
								<div className="col-span-2">
									{startDate && format(new Date(startDate), "do MMM yyyy")} -{" "}
									{endDate && endDate != "null" ? format(new Date(endDate), "do MMM yyyy") : "Now"}
								</div>
							</div>
						),
					)}
				</div>
			) : null}
		</>
	);
}

function ErrorRows({ errors = [], warnings = [] }) {
	return (
		<>
			{errors?.length ? (
				<div className="grid grid-cols-subgrid col-span-6 gap-2 p-4 form-section">
					<h4 className="col-span-6">Errors</h4>
					{errors.map(({ message }) => (
						<div className="col-span-6 px-4 pt-4 pb-2 bg-white">{message}</div>
					))}
				</div>
			) : null}
			{warnings?.length ? (
				<div className="grid grid-cols-subgrid col-span-6 gap-2 p-4 form-section">
					<h4 className="col-span-6">Warnings</h4>
					{warnings.map(({ message }) => (
						<div className="col-span-6 px-4 pt-4 pb-2 bg-white">{message}</div>
					))}
				</div>
			) : null}
		</>
	);
}
